import { MAX_PUTT_DISTANCE_FT } from './distanceLimits';
/**
 * Simulated 9 — course catalog.
 *
 * IMPORTANT: course IDs and hole distances are PERMANENT once leaderboards go
 * live. Never edit an existing course's holes; add a new course instead
 * (that's also how contour "Championship" variants arrive later without
 * resetting flat boards).
 *
 * 2026-07-16 catalog cull (Cole): the original ten classic-scene courses were
 * REMOVED and the ten realistic builds renumbered as courses 1–10 (scores on
 * the removed IDs were deliberately tossed — pre-launch data, nobody cared).
 * The surviving IDs keep their historical `_v2` names because IDs are
 * permanent; only the display names dropped the "II" suffix.
 *
 * Par rule: under 8 ft = par 1 (you're expected to hole it), 60 ft = par 3
 * (true lag territory at the product ceiling), else par 2. Max two par 1s
 * and two par 3s per course.
 */

export type Sim9SceneryId =
  | 'cedar'
  | 'island'
  | 'desert'
  | 'links'
  | 'city'
  | 'alpine'
  | 'pine'
  | 'autumn'
  | 'twilight'
  | 'storm';

export interface Sim9Course {
  id: string;
  name: string;
  themeId: string;
  tagline: string;
  /** Opts this course into the realistic scenery profile for its biome. */
  sceneryId?: Sim9SceneryId;
  /** Hole distances in ft. Order is the playing order. */
  holes: number[];
}

// The Simulated 9 gimme: a leave at or inside it finishes the hole with a
// tap-in stroke. Sim 9 only (event Sim 9 included); no other mode reads it.
// 5.0 since 2026-10-19 (Cole: "Slow putts often don't roll true on mats and
// thus the start line data is junk"). 3.0 before that (was 2.0 until 2026-07:
// distanceFt includes the 2 ft setup, so a "2.1 ft" required putt meant
// stopping the ball 0.1 ft past the gate, which is physically unreachable).
// Rounds and records played at 3 ft keep their scores.
export const GIMME_FT = 5.0;
/** The gimme of app builds from before 2026-10-19. A friend challenge sent
 *  from one is answered at this gimme (functions/src/lib/sim9Gimme.ts). */
export const SIM9_LEGACY_GIMME_FT = 3.0;
/** The gimmes this build can play, sent as `sim9GimmeFts` so the server can
 *  keep both players of a match on one rule. */
export const SIM9_PLAYABLE_GIMMES_FT: readonly number[] = [SIM9_LEGACY_GIMME_FT, GIMME_FT];
/** A stored or served gimme: one the app has shipped, else the old 3 ft. */
export function sim9GimmeFtOf(raw: unknown): number {
  const value = Number(raw);
  return value === GIMME_FT || value === SIM9_LEGACY_GIMME_FT ? value : SIM9_LEGACY_GIMME_FT;
}

export function parForDistance(distanceFt: number): number {
  if (distanceFt < 8) return 1;
  // >= 60, not > 60 (Cole, 2026-08-03): the long holes were capped AT the
  // 60 ft ceiling, and they must stay par 3 or every course's published par
  // would shift and invalidate historical to-par scores. No course had a
  // hole at exactly 60 before the cap, so nothing else re-grades.
  if (distanceFt >= MAX_PUTT_DISTANCE_FT) return 3;
  return 2;
}

export function coursePar(course: Sim9Course): number {
  return course.holes.reduce((sum, d) => sum + parForDistance(d), 0);
}

export const SIM9_COURSES: Sim9Course[] = [
  {
    id: 'cedarridge',
    name: 'Cedar Ridge',
    themeId: 'cedar',
    tagline: 'Cedar-lined precision.',
    sceneryId: 'cedar',
    holes: [10, 22, 34, 16, 42, 28, 60, 36, 18], // par 19 (1x par3)
  },
  {
    id: 'sandpiper_v2',
    name: 'Sandpiper Isle',
    themeId: 'island',
    tagline: 'Palms, trade winds, one brutal lag.',
    sceneryId: 'island',
    holes: [6, 22, 30, 14, 60, 18, 26, 38, 12], // par 18 (1x par1, 1x par3)
  },
  {
    id: 'redmesa_v2',
    name: 'Red Mesa',
    themeId: 'desert',
    tagline: 'Long, bright, and bouncy.',
    sceneryId: 'desert',
    holes: [45, 20, 33, 16, 40, 24, 50, 28, 36], // par 18 — reshuffled 2026-07-15 (same 9 distances; the old order rose in three long waves and felt like 'every hole gets longer' in family testing)
  },
  {
    id: 'links_v2',
    name: 'The Links',
    themeId: 'links',
    tagline: 'Two monsters in the dunes.',
    sceneryId: 'links',
    holes: [28, 60, 24, 35, 18, 60, 30, 42, 22], // par 20 (2x par3)
  },
  {
    id: 'midtown_v2',
    name: 'Midtown',
    themeId: 'city',
    tagline: 'Target golf downtown. Convert or pay.',
    sceneryId: 'city',
    holes: [7, 15, 20, 6, 12, 25, 18, 10, 22], // par 16 (2x par1)
  },
  {
    id: 'alpine_v2',
    name: 'Alpine',
    themeId: 'alpine',
    tagline: 'Thin air, crisp reads.',
    sceneryId: 'alpine',
    // Hole 1 was 5 ft until 2026-10-19 (Cole: "move the 5ft shortest to 6ft"),
    // one foot past the 5 ft gimme. Still par 1, so the par stays 18.
    holes: [6, 24, 31, 60, 19, 27, 13, 38, 23], // par 18 (1x par1, 1x par3)
  },
  {
    id: 'pineland_v2',
    name: 'Pineland',
    themeId: 'pine',
    tagline: 'Classic golf between the conifers.',
    sceneryId: 'pine',
    holes: [14, 26, 19, 32, 22, 39, 17, 29, 44], // par 18
  },
  {
    id: 'autumnglen_v2',
    name: 'Autumn Glen',
    themeId: 'autumn',
    tagline: 'Falling leaves, climbing distances.',
    sceneryId: 'autumn',
    holes: [25, 38, 30, 60, 21, 33, 45, 27, 41], // par 19 (1x par3)
  },
  {
    id: 'twilight_v2',
    name: 'Twilight Muni',
    themeId: 'twilight',
    tagline: 'Under the lights. Two gimmes, one haul.',
    sceneryId: 'twilight',
    holes: [7, 16, 24, 60, 12, 6, 20, 28, 15], // par 17 (2x par1, 1x par3)
  },
  {
    id: 'gauntlet_v2',
    name: 'The Gauntlet',
    themeId: 'storm',
    tagline: 'The finale. Survive the lags.',
    sceneryId: 'storm',
    holes: [35, 60, 42, 28, 55, 60, 38, 48, 30], // par 20 (2x par3)
  },
];

// ⛔ RETIRED 2026-08-09: 'cubhouse_v1' ("The Cubhouse") was the junior course
// — nine holes all inside 15 ft. TEE BOXES replaced it: every course now has
// black/blue/white/red tees, so any of the ten can be played at junior length
// (Cole: "let's remove the cubhouse once we build this"). Do not reissue the
// id. `courseById` returns undefined for it, which every caller already
// handles with `?.` or a fallback — a saved round on it simply won't resume.
export function courseById(id: string): Sim9Course | undefined {
  return SIM9_COURSES.find((c) => c.id === id);
}

/** A uniformly random course from the live catalog. */
export function randomCourse(): Sim9Course {
  return SIM9_COURSES[Math.floor(Math.random() * SIM9_COURSES.length)];
}
